'use client';

/**
 * The one way a free tool hands a generated file to the visitor (DEV-15241).
 *
 * WHY THIS EXISTS. Every image tool used to do the same thing: build a
 * temporary `<a download>` and `.click()` it. That works on desktop and on
 * Android, but on an iPhone it fails in several ways at once:
 *  - Chrome, Firefox and every in-app browser on iOS (Instagram, Facebook,
 *    Gmail) ignore `download` on a `data:` URL, so nothing happens at all.
 *  - Safari asks "Do you want to download?" and then files the image in
 *    Files > Downloads, never in Photos, which is where people look for it.
 *  - Revoking the object URL one second after the click kills the download
 *    while Safari is still waiting for that "Download" tap.
 * So on iOS we open the native share sheet instead, which offers "Save Image"
 * straight into Photos. If iOS refuses the share sheet (it only allows one
 * shortly after a tap, and a slow render can outlast that window) we show the
 * image in an overlay with a fresh "Save image" button and a press-and-hold
 * hint, both of which always work.
 *
 * Everywhere else the old `<a download>` path is kept, with a long revoke delay.
 */

export type SaveResult = 'downloaded' | 'shared' | 'preview' | 'cancelled';

/** iPhone, iPod, and iPad (which reports itself as a Mac with a touch screen). */
export const isIOS = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  return navigator.platform === 'MacIntel' && (navigator.maxTouchPoints || 0) > 1;
};

/** Any WebKit-rendered browser: every browser on iOS, plus desktop Safari. */
const isWebKit = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  if (isIOS()) return true;
  const ua = navigator.userAgent || '';
  return /safari/i.test(ua) && !/chrome|chromium|crios|android|edg/i.test(ua);
};

const canShareFile = (file: File): boolean => {
  try {
    return typeof navigator.share === 'function' && !!navigator.canShare?.({ files: [file] });
  } catch {
    return false;
  }
};

const anchorDownload = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Long on purpose. Safari keeps the URL pending until the visitor answers its
  // "Download?" prompt, and a short revoke cancels the file under them.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
};

/**
 * Fallback for iOS when the share sheet is not available or not allowed: show
 * the finished image on top of the page. A fresh tap on "Save image" restores
 * the permission the share sheet needs, and press-and-hold on the image works in
 * every iOS browser, in-app browsers included.
 */
const showPreview = (blob: Blob, filename: string): Promise<SaveResult> =>
  new Promise((resolve) => {
    const url = URL.createObjectURL(blob);
    const file = new File([blob], filename, { type: blob.type || 'image/png' });
    const shareable = canShareFile(file);

    const overlay = document.createElement('div');
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', 'Save your image');
    overlay.style.cssText =
      'position:fixed;inset:0;z-index:2147483647;background:rgba(15,23,42,.97);display:flex;' +
      'flex-direction:column;align-items:center;justify-content:center;gap:14px;padding:20px 16px;' +
      "font-family:Poppins,'Open Sans',system-ui,sans-serif;-webkit-touch-callout:default;";

    const img = document.createElement('img');
    img.src = url;
    img.alt = filename;
    img.style.cssText =
      'max-width:100%;max-height:62vh;object-fit:contain;border-radius:10px;background:#fff;' +
      'box-shadow:0 10px 30px rgba(0,0,0,.35);-webkit-touch-callout:default;user-select:auto;';

    const hint = document.createElement('p');
    hint.textContent = shareable
      ? 'Tap "Save image", or press and hold the picture and choose "Save to Photos".'
      : 'Press and hold the picture, then choose "Save to Photos".';
    hint.style.cssText = 'margin:0;color:#fff;font-size:15px;line-height:1.45;text-align:center;max-width:420px;';

    const row = document.createElement('div');
    row.style.cssText = 'display:flex;gap:10px;flex-wrap:wrap;justify-content:center;';

    const button = (label: string, primary: boolean) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.textContent = label;
      b.style.cssText =
        'min-height:44px;padding:0 22px;border-radius:999px;font-size:15px;font-weight:600;cursor:pointer;' +
        (primary ? 'background:#4c4fff;color:#fff;border:0;' : 'background:transparent;color:#fff;border:1px solid rgba(255,255,255,.6);');
      return b;
    };

    let result: SaveResult = 'preview';
    const close = () => {
      overlay.remove();
      document.removeEventListener('keydown', onKey);
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      resolve(result);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };

    if (shareable) {
      const save = button('Save image', true);
      save.onclick = async () => {
        try {
          await navigator.share({ files: [file] });
          result = 'shared';
          close();
        } catch {
          /* cancelled, or refused: the press-and-hold route is still on screen */
        }
      };
      row.appendChild(save);
    }
    const done = button('Done', !shareable);
    done.onclick = close;
    row.appendChild(done);

    overlay.onclick = (e) => {
      if (e.target === overlay) close();
    };
    document.addEventListener('keydown', onKey);
    overlay.append(img, hint, row);
    document.body.appendChild(overlay);
  });

/**
 * Hand a finished file to the visitor in whatever way their device supports.
 * Resolves to what happened, so callers can count a completion only when one is
 * likely ('cancelled' means the visitor closed the iOS share sheet themselves).
 */
export const saveFile = async (blob: Blob, filename: string): Promise<SaveResult> => {
  if (!isIOS()) {
    anchorDownload(blob, filename);
    return 'downloaded';
  }

  const file = new File([blob], filename, { type: blob.type || 'application/octet-stream' });
  if (!canShareFile(file)) {
    // A zip and similar non-image files: Safari's download prompt files these
    // in Files > Downloads, which is the right home for them.
    if (!blob.type.startsWith('image/')) {
      anchorDownload(blob, filename);
      return 'downloaded';
    }
    return showPreview(blob, filename);
  }

  try {
    await navigator.share({ files: [file] });
    return 'shared';
  } catch (err) {
    if ((err as DOMException)?.name === 'AbortError') return 'cancelled';
    // NotAllowedError: the render outlasted the tap's permission window.
    return showPreview(blob, filename);
  }
};

/** Promise wrapper for `canvas.toBlob`, which rejects instead of passing null. */
export const canvasToBlob = (canvas: HTMLCanvasElement, type = 'image/png', quality?: number): Promise<Blob> =>
  new Promise((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('canvas.toBlob returned null'))), type, quality);
  });

/** `data:` URL to Blob, for tools that already hold their output as a data URL. */
export const dataUrlToBlob = async (dataUrl: string): Promise<Blob> => (await fetch(dataUrl)).blob();

// iOS refuses to draw a canvas bigger than about 16.7 million pixels and hands
// back a blank one instead. Stay under it with some margin.
const IOS_MAX_CANVAS_PIXELS = 16_000_000;

type HtmlToImageOptions = NonNullable<Parameters<typeof import('html-to-image').toBlob>[1]>;

/**
 * `html-to-image` export that also works on iPhone.
 *  - WebKit decodes the images inside the export SVG lazily, so the FIRST render
 *    often comes out without the avatar or photos. A throwaway render first
 *    warms them up (the widely used workaround for html-to-image on Safari).
 *  - On iOS the pixel ratio is lowered when needed to stay under the canvas
 *    size limit, which otherwise produces a blank image for tall content.
 */
export const renderNodeToBlob = async (node: HTMLElement, options: HtmlToImageOptions = {}): Promise<Blob> => {
  const { toBlob, toCanvas } = await import('html-to-image');
  const opts: HtmlToImageOptions = { ...options };

  if (isIOS()) {
    const w = node.scrollWidth || node.offsetWidth;
    const h = node.scrollHeight || node.offsetHeight;
    const requested = opts.pixelRatio ?? (window.devicePixelRatio || 1);
    if (w > 0 && h > 0) {
      const max = Math.sqrt(IOS_MAX_CANVAS_PIXELS / (w * h));
      opts.pixelRatio = Math.max(0.5, Math.min(requested, max));
    }
  }

  if (isWebKit()) {
    try {
      await toCanvas(node, { ...opts, pixelRatio: 1 });
    } catch {
      /* the real render below reports any genuine failure */
    }
  }

  const blob = await toBlob(node, opts);
  if (!blob) throw new Error('html-to-image returned no image');
  return blob;
};
